'use client';

import { useState, type MouseEvent, type ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { SourceCodeIcon, Globe02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { HttpArtifact } from '../../../../artifacts/http/HttpArtifact';
import { useChatHarOptional } from '../../../har';
import { PythonExecutionCard } from '../../../../artifacts/python/PythonArtifact';
import { resolveCoderDisplayResult } from '../../../trace-view/utils/coder-tool-output';
import type { CardRenderContext } from '../../tool-config-types';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { toPrettyText, toRecord } from '../shared/network-tool-card-utils';

type PythonToolInput = {
  script?: string;
  code?: string;
};
type PythonExecutionOutput = {
  success?: boolean;
  result?: unknown;
  codeRuntime?: number;
  stdout?: unknown[];
  stderr?: unknown[];
};
type PythonToolOutput = PythonToolInput & {
  result?: unknown;
  output?: PythonExecutionOutput;
};

export const renderPythonCard = (ctx: CardRenderContext): ReactNode => {
  return <PythonLikeCard ctx={ctx} />;
};

export const renderCoderCard = (ctx: CardRenderContext): ReactNode => {
  return <PythonLikeCard ctx={ctx} />;
};

function readPythonToolInput(value: unknown): PythonToolInput | undefined {
  const record = toRecord(value);

  if (!record) return undefined;

  return {
    script: typeof record.script === 'string' ? record.script : undefined,
    code: typeof record.code === 'string' ? record.code : undefined,
  };
}

function readPythonExecutionOutput(
  value: unknown,
): PythonExecutionOutput | undefined {
  const record = toRecord(value);

  if (!record) return undefined;

  return {
    success: typeof record.success === 'boolean' ? record.success : undefined,
    result: record.result,
    codeRuntime:
      typeof record.codeRuntime === 'number' ? record.codeRuntime : undefined,
    stdout: Array.isArray(record.stdout) ? record.stdout : undefined,
    stderr: Array.isArray(record.stderr) ? record.stderr : undefined,
  };
}

function readPythonToolOutput(value: unknown): PythonToolOutput | undefined {
  const record = toRecord(value);

  if (!record) return undefined;

  return {
    ...readPythonToolInput(record),
    result: record.result,
    output: readPythonExecutionOutput(record.output),
  };
}

function PythonCardHeaderActions({
  hitlActions,
  executionSettled,
  onShowCode,
  onShowHttp,
  showHttpView,
  toolCallId,
}: {
  hitlActions: ReactNode;
  executionSettled: boolean;
  onShowCode: () => void;
  onShowHttp: () => void;
  showHttpView: boolean;
  toolCallId: string | undefined;
}): ReactNode {
  const t = useTranslations('designSystem.artifacts');

  if (!executionSettled || !toolCallId) {
    return hitlActions;
  }

  const handleShowCode = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onShowCode();
  };

  const handleShowHttp = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onShowHttp();
  };

  return (
    <HStack gap={1} alignItems="center">
      {hitlActions}
      <HStack
        gap="0"
        borderWidth="1px"
        borderColor="border.neutral.muted"
        borderRadius="sm"
        overflow="hidden"
        flexShrink={0}
      >
        <Box
          as="button"
          aria-label={t('showCode')}
          onClick={handleShowCode}
          h="5"
          w="5"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          bg={!showHttpView ? 'bg.muted' : 'transparent'}
          color={!showHttpView ? 'fg.default' : 'fg.muted'}
          _hover={{
            bg: !showHttpView ? 'bg.muted' : 'bg.hover',
          }}
        >
          <SourceCodeIcon size={10} />
        </Box>
        <Box
          as="button"
          aria-label={t('showHttpRequests')}
          onClick={handleShowHttp}
          h="5"
          w="5"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          bg={showHttpView ? 'bg.muted' : 'transparent'}
          color={showHttpView ? 'fg.default' : 'fg.muted'}
          _hover={{
            bg: showHttpView ? 'bg.muted' : 'bg.hover',
          }}
        >
          <Globe02Icon size={10} />
        </Box>
      </HStack>
    </HStack>
  );
}

function PythonLikeCard({ ctx }: { ctx: CardRenderContext }): ReactNode {
  const tCoder = useTranslations('designSystem.chat.coder');
  const [showHttpView, setShowHttpView] = useState(false);
  const chatHar = useChatHarOptional();
  const input = readPythonToolInput(ctx.toolCallData.input);
  const output = readPythonToolOutput(ctx.toolCallData.output);
  const nestedOutput = output?.output;
  const toolCallId = ctx.toolCallData.toolCallId ?? ctx.toolCallData.id;
  const executionSettled =
    ctx.toolCallData.status === 'completed' ||
    ctx.toolCallData.status === 'error';
  const showHttpToggle =
    !!toolCallId && (chatHar?.hasHarPresence?.(toolCallId) ?? false);
  const code =
    output?.script || output?.code || input?.script || input?.code || '';
  const structuredResult = resolveCoderDisplayResult({
    output: ctx.toolCallData.output,
  });
  const result =
    structuredResult ||
    toPrettyText(nestedOutput?.result ?? output?.result) ||
    '';
  const pythonRuntime =
    nestedOutput?.codeRuntime ?? ctx.toolCallData.timing?.durationMs;
  const hasError =
    ctx.toolCallData.status === 'error' ||
    (nestedOutput?.success === undefined ? false : !nestedOutput.success);
  const isResultPending =
    (ctx.toolCallData.status === 'started' ||
      ctx.toolCallData.status === 'in-progress') &&
    !ctx.toolCallData.error &&
    ctx.hitlDecisionType !== 'edit' &&
    !(ctx.headerActions && !ctx.hitlDecisionType);
  const hitlActions = getHITLArtifactHeaderActions(ctx);
  const headerActions =
    executionSettled && toolCallId && showHttpToggle ? (
      <PythonCardHeaderActions
        hitlActions={hitlActions}
        executionSettled={executionSettled}
        onShowCode={() => setShowHttpView(false)}
        onShowHttp={() => setShowHttpView(true)}
        showHttpView={showHttpView}
        toolCallId={toolCallId}
      />
    ) : (
      hitlActions
    );

  return (
    <PythonExecutionCard
      code={code}
      codeLanguage="python"
      result={result}
      resultLanguage="text"
      error={ctx.toolCallData.error}
      hasError={hasError}
      errorLabel={
        nestedOutput?.success === false ? tCoder('executionFailed') : undefined
      }
      pythonRuntime={pythonRuntime}
      headerActions={headerActions}
      isResultPending={isResultPending}
      body={
        showHttpView && executionSettled && toolCallId && showHttpToggle ? (
          <Box maxH="420px" overflow="auto">
            <HttpArtifact
              toolCallId={toolCallId}
              toolType="PYTHON"
              showSummaryHeader={true}
              embedded
            />
          </Box>
        ) : undefined
      }
    />
  );
}
